<template>
  <div class="chart-inner" ref="progressDonut" />
</template>

<script>
  /* eslint-disable */
  import * as d3 from "d3"

  export default {
    props: {
      progress: [String, Number],
      previous: [String, Number],
      colorOverride: {type: String},
      percentColorOverride: {type: String},
      isDysphotopsia: {required: false, type: Boolean, default: false},
      hideRing: {required: false, type: Boolean, default: false},
      hidePercent: {required: false, type: Boolean, default: false},
      mob: [Boolean]
    },

    watch: {
      progress(newVal) {
        this.$refs.progressDonut.innerHTML = ""
        this.draw()
      },
      previous(newVal) {
        this.$refs.progressDonut.innerHTML = ""
        this.draw()
      }
    },

    mounted() {
      this.draw()
    },

    methods: {
      draw() {
        const tau = 2 * Math.PI
        const element = this.$el
        const width = 550
        const height = 400
        const radius = width / 2
        const viewBox = `0 0 ${width} ${height}`
        const fontSize = (this.hideRing) ? width / 3 : width / 4.2
        const dysphotopsia = this.isDysphotopsia

        const arc = d3.arc()
          .innerRadius(radius - (this.mob ? 85 : 91))
          .outerRadius(radius - 110)
          .startAngle(0)

        const arcInner = d3.arc()
          .innerRadius(radius - 22)
          .outerRadius(radius - 38)
          .startAngle(0)

        const svg = d3.select(element)
          .append("svg")
          .attr("viewBox", viewBox)

        const g = svg.append("g")
          .attr("transform", `translate(${width / 2}, ${height / 2})`)

        const text = g.append("text")
          .text(this.progress === "N/A" ? "N/A" : "0")
          .attr("class", "percentage-text")
          .attr("text-anchor", "middle")
          .style("font-size", fontSize + "px")
          .style("font-weight", "normal")
          .attr("dy", (height / 10) + 5)

        if (this.progress !== "N/A" && !this.hidePercent) {
          if (this.percentColorOverride) {
            g.append("text")
              .text("%")
              .attr("fill", this.percentColorOverride)
              .style("font-size", this.hideRing ? fontSize / 2 + "px" : fontSize / 3 + "px")
              .style("font-weight", "bold")
              .attr("x", this.hideRing ? 100 : 70)
          } else {
            g.append("text")
              .text("%")
              .attr("class", "percentage-text")
              .style("font-size", this.hideRing ? fontSize / 2 + "px" : fontSize / 3 + "px")
              .style("font-weight", "bold")
              .attr("x", this.hideRing ? 100 : 70)
          }
        }

        if (this.progress !== "N/A" && !this.hideRing) {
          const background = g.append("path")
          .datum({endAngle: 0})
          .style("fill", "#ddd")
          .attr("d", arcInner)
          .transition()
          .delay(200)
          .duration(800)
          .ease(d3.easeCubicIn)
          .attrTween("d", arcTween(tau))
        }

        if (this.progress !== "N/A" && !this.hideRing) {
          if (this.colorOverride) {
            const foreground = g.append("path")
            .datum({endAngle: 0})
            .attr("d", arc)
            .attr("fill", this.colorOverride)
            .transition()
            .delay(1050)
            .duration(1500)
            .ease(d3.easeCubicOut)
            .attrTween("d", arcTween((this.progress / 100) * tau))
          } else {
            const foreground = g.append("path")
            .datum({endAngle: 0})
            .attr("d", arc)
            .attr("class", addPathClass(this.progress))
            .transition()
            .delay(1050)
            .duration(1500)
            .ease(d3.easeCubicOut)
            .attrTween("d", arcTween((this.progress / 100) * tau))
          }
        }

        function arcTween(newAngle) {
          return function (d) {
            const interpolate = d3.interpolate(d.endAngle, newAngle)
            return function (t) {
              d.endAngle = interpolate(t)
              return arc(d)
            }
          }
        }

        function addPathClass(value) {
          if(dysphotopsia) {
            if(value <= 25) {
              return "color-very-satisfied"
            } else if(value <= 40) {
              return "color-satisfied"
            } else if(value <= 55) {
              return "color-neutral"
            } else if(value <= 80) {
              return "color-diss"
            } else {
              return "color-very-diss"
            }
          }

          if(value <= 25) {
            return "color-very-diss"
          } else if(value <= 40) {
            return "color-diss"
          } else if(value <= 55) {
            return "color-neutral"
          } else if(value <= 80) {
            return "color-satisfied"
          } else {
            return "color-very-satisfied"
          }
        }

        const format = d3.format(",d")

        const tweenElement = this.$refs.progressDonut
        const maxValue = this.progress

        if (this.progress !== "N/A") {
          const numberAnimate = d3.select(tweenElement)
          .transition()
          .duration(2500)
          .on("start", function repeat() {
            d3.active(this)
              .tween("text", function () {
                const i = d3.interpolateNumber(text.text().replace(/,/g, ""), maxValue)
                return function (t) { text.text(format(i(t))) }
              })
              .transition()
              .delay(1500)
              .on("start", repeat)
          })
        }

        if(this.previous !== undefined && this.previous !== "N/A" &&
          this.progress !== undefined && this.progress !== "N/A") {
          let dataset = [
            {x: 1, y: this.previous},
            {x: 2, y: this.progress}
          ]

          if(this.previous === 'under_10_answers') {
            dataset = [
            {x: 1, y: 80},
            {x: 2, y: 100}
            ]
            if(this.isDysphotopsia) {
              dataset = [
              {x: 1, y: 20},
              {x: 2, y: 10}
              ]
            }
          }

          const options = {
            margin : {top: 0, right: 0, bottom: 0, left: 0},
            height: 0,
            width: 300
          }
          const margin = {
            top: options.margin.top || 0,
            right: options.margin.right || 0,
            bottom: options.margin.bottom || 0,
            left: options.margin.left || 0
          }
          const width2 = options.width - margin.left - margin.right
          const height2 = options.height - margin.top - margin.bottom

          const randomNumber = Math.floor(Math.random() * 10000000)

          const xScale = d3.scaleLinear()
            .domain([0, getMaxValue(dataset, "x")])
            .range([0, 60])

          const yScale = d3.scaleLinear()
            .domain([0, getMaxValue(dataset, "y")])
            .range([25, 0])

          /* const g2 = svg.append("g")
            .attr("transform", "translate(260,299)") */

          const line = d3.line()
            .x((d, i) => xScale(i))
            .y((d, i) => yScale(d.y))

          /* const linearGradient = g2.append("defs")
            .append("linearGradient")
            .attr("id", `linear-gradient${randomNumber}`)

          linearGradient.append("stop")
            .attr("offset", "30%")
            .attr("stop-color", pickColor(dataset[0].y))

          linearGradient.append("stop")
            .attr("offset", "70%")
            .attr("stop-color", pickColor(dataset[1].y))

          g2.append("path")
            .datum(dataset)
            .attr("class", "line")
            .attr("d", line)
            .attr("stroke", `url(#linear-gradient${randomNumber})`)
            .attr("fill", "none")

          g2.selectAll(".dot")
            .data(dataset)
            .enter().append("circle")
            .attr("class", "dot")
            .attr("cx", (d, i) => xScale(i))
            .attr("cy", (d, i) => yScale(d.y))
            .attr("r", 6)
            .attr("stroke", (d, i) => pickColor(d.y))
            .attr("fill", "#fff") */
        }

        function getMaxValue(dataset, key) {
          const max = dataset.reduce((a, b) => (a[key] > b[key]) ? a : b)
          return max[key]
        }

        function pickColor(value) {
          if(dysphotopsia) {
            if(value <= 25) {
              return "#21931B"
            } else if(value <= 40) {
              return "#8BC34A"
            } else if(value <= 55) {
              return "#FFD167"
            } else if(value <= 80) {
              return "#F27830"
            } else {
              return "#C60E2B"
            }
          }

          if(value <= 25) {
            return "#C60E2B"
          } else if(value <= 40) {
            return "#F27830"
          } else if(value <= 55) {
            return "#FFD167"
          } else if(value <= 80) {
            return "#8BC34A"
          } else {
            return "#21931B"
          }
        }
      }
    }
  }
  /* eslint-enable */
</script>

<style lang="sass">
  @import "../../../styles/helpers/_mixins.sass"
  @import "../../../styles/base/variables"

  .chart-inner
    display: inline-block
    position: relative
    width: 100%
    vertical-align: top
    overflow: hidden
    height: 100%

    svg
      height: 100%
      width: 100%
      margin-left: auto

  .percentage-text
    fill: $color-side-nav

  .color-default
    fill: $color-body-bg

  .color-very-diss
    fill: $color-very-diss

  .color-diss
    fill: $color-diss

  .color-neutral
    fill: $color-neutral

  .color-satisfied
    fill: $color-satisfied

  .color-very-satisfied
    fill: $color-very-satisfied

  .line
    stroke-width: 6

  .dot
    transition: all .05s ease-in-out
    stroke-width: 6

</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/charts/PieProgressChart.vue